import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, X, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface Patient {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  insurance_type: string;
}

interface CPTCode {
  code: string;
  description: string;
  defaultAmount: number;
}

// CPT codes organized by service type
const cptCodesByService: Record<string, CPTCode[]> = {
  telehealth: [
    { code: "99211", description: "Office visit, minimal (5 min)", defaultAmount: 45 },
    { code: "99212", description: "Office visit, straightforward (10-19 min)", defaultAmount: 75 },
    { code: "99213", description: "Office visit, low complexity (20-29 min)", defaultAmount: 125 },
    { code: "99214", description: "Office visit, moderate (30-39 min)", defaultAmount: 175 },
    { code: "99215", description: "Office visit, high complexity (40-54 min)", defaultAmount: 225 },
    { code: "99441", description: "Telephone E/M (5-10 min)", defaultAmount: 45 },
    { code: "99442", description: "Telephone E/M (11-20 min)", defaultAmount: 75 },
    { code: "99443", description: "Telephone E/M (21-30 min)", defaultAmount: 110 },
  ],
  behavioral: [
    { code: "90832", description: "Psychotherapy, 16-37 min", defaultAmount: 95 },
    { code: "90834", description: "Psychotherapy, 38-52 min", defaultAmount: 150 },
    { code: "90837", description: "Psychotherapy, 53+ min", defaultAmount: 200 },
    { code: "90839", description: "Crisis psychotherapy, first 60 min", defaultAmount: 275 },
    { code: "90840", description: "Crisis psychotherapy, add'l 30 min", defaultAmount: 140 },
    { code: "90847", description: "Family psychotherapy w/ patient", defaultAmount: 175 },
    { code: "90853", description: "Group psychotherapy", defaultAmount: 55 },
  ],
  mental: [
    { code: "90791", description: "Psychiatric diagnostic evaluation", defaultAmount: 250 },
    { code: "90792", description: "Psychiatric diagnostic eval w/ medical", defaultAmount: 300 },
    { code: "90833", description: "Psychotherapy add-on, 16-37 min", defaultAmount: 75 },
    { code: "90836", description: "Psychotherapy add-on, 38-52 min", defaultAmount: 125 },
    { code: "90838", description: "Psychotherapy add-on, 53+ min", defaultAmount: 165 },
    { code: "90863", description: "Pharmacologic management", defaultAmount: 50 },
  ],
  rpm: [
    { code: "99453", description: "RPM setup & patient education", defaultAmount: 20 },
    { code: "99454", description: "RPM device supply w/ daily recording", defaultAmount: 55 },
    { code: "99457", description: "RPM treatment mgmt, first 20 min", defaultAmount: 50 },
    { code: "99458", description: "RPM treatment mgmt, add'l 20 min", defaultAmount: 40 },
    { code: "99091", description: "Collection/interpretation of data (30+ min)", defaultAmount: 60 },
  ],
  evaluation: [
    { code: "99201", description: "New patient, straightforward", defaultAmount: 75 },
    { code: "99202", description: "New patient, straightforward (15-29 min)", defaultAmount: 110 },
    { code: "99203", description: "New patient, low complexity (30-44 min)", defaultAmount: 175 },
    { code: "99204", description: "New patient, moderate (45-59 min)", defaultAmount: 250 },
    { code: "99205", description: "New patient, high complexity (60-74 min)", defaultAmount: 325 },
    { code: "99385", description: "Initial preventive visit, 18-39 yrs", defaultAmount: 200 },
    { code: "99386", description: "Initial preventive visit, 40-64 yrs", defaultAmount: 225 },
    { code: "99387", description: "Initial preventive visit, 65+ yrs", defaultAmount: 250 },
  ],
};

const serviceTypes = [
  { value: "telehealth", label: "Telehealth Session" },
  { value: "behavioral", label: "Behavioral Health" },
  { value: "mental", label: "Mental Health" },
  { value: "rpm", label: "Remote Monitoring" },
  { value: "evaluation", label: "Initial Evaluation" },
];

interface ClaimSubmissionFormProps {
  onSubmit: (data: ClaimFormData) => void;
  onCancel: () => void;
}

export interface ClaimFormData {
  patientId: string;
  patientName: string;
  serviceType: string;
  cptCodes: CPTCode[];
  totalAmount: number;
  notes: string;
}

export function ClaimSubmissionForm({ onSubmit, onCancel }: ClaimSubmissionFormProps) {
  const [patientOpen, setPatientOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [patientSearch, setPatientSearch] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [selectedCodes, setSelectedCodes] = useState<CPTCode[]>([]);
  const [codeOpen, setCodeOpen] = useState(false);
  const [notes, setNotes] = useState("");

  // Fetch patients from intake_forms
  const { data: patients = [], isLoading: patientsLoading } = useQuery({
    queryKey: ["patients-for-billing"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("patients")
        .select("id, user_id, first_name, last_name, insurance_type")
        .order("last_name", { ascending: true });

      if (error) throw error;
      return data as Patient[];
    },
  });

  // Filter patients based on search
  const filteredPatients = useMemo(() => {
    if (!patientSearch) return patients;
    const search = patientSearch.toLowerCase();
    return patients.filter(
      (p) =>
        p.first_name.toLowerCase().includes(search) ||
        p.last_name.toLowerCase().includes(search)
    );
  }, [patients, patientSearch]);

  // Get available CPT codes based on selected service type
  const availableCodes = useMemo(() => {
    if (!serviceType) return [];
    return cptCodesByService[serviceType] || [];
  }, [serviceType]);

  // Calculate total amount
  const totalAmount = useMemo(() => {
    return selectedCodes.reduce((sum, code) => sum + code.defaultAmount, 0);
  }, [selectedCodes]);

  const handleAddCode = (code: CPTCode) => {
    if (!selectedCodes.find((c) => c.code === code.code)) {
      setSelectedCodes([...selectedCodes, code]);
    }
    setCodeOpen(false);
  };

  const handleRemoveCode = (codeToRemove: string) => {
    setSelectedCodes(selectedCodes.filter((c) => c.code !== codeToRemove));
  };

  const handleServiceTypeChange = (value: string) => {
    setServiceType(value);
    setSelectedCodes([]); // Clear selected codes when service type changes
  };

  const handleSubmit = () => {
    if (!selectedPatient || !serviceType || selectedCodes.length === 0) return;

    onSubmit({
      patientId: selectedPatient.user_id,
      patientName: `${selectedPatient.first_name} ${selectedPatient.last_name}`,
      serviceType: serviceTypes.find((s) => s.value === serviceType)?.label || serviceType,
      cptCodes: selectedCodes,
      totalAmount,
      notes,
    });
  };

  const isValid = selectedPatient && serviceType && selectedCodes.length > 0;

  return (
    <div className="grid gap-4 py-4">
      {/* Patient Selection */}
      <div className="grid gap-2">
        <Label>Patient Name</Label>
        <Popover open={patientOpen} onOpenChange={setPatientOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={patientOpen}
              className="w-full justify-between"
            >
              {selectedPatient
                ? `${selectedPatient.first_name} ${selectedPatient.last_name}`
                : "Search for patient..."}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0" align="start">
            <Command>
              <CommandInput
                placeholder="Search patients..."
                value={patientSearch}
                onValueChange={setPatientSearch}
              />
              <CommandList>
                <CommandEmpty>
                  {patientsLoading ? "Loading..." : "No patient found."}
                </CommandEmpty>
                <CommandGroup>
                  {filteredPatients.map((patient) => (
                    <CommandItem
                      key={patient.id}
                      value={`${patient.first_name} ${patient.last_name}`}
                      onSelect={() => {
                        setSelectedPatient(patient);
                        setPatientOpen(false);
                        setPatientSearch("");
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedPatient?.id === patient.id
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col">
                        <span>
                          {patient.first_name} {patient.last_name}
                        </span>
                        <span className="text-xs text-muted-foreground capitalize">
                          {patient.insurance_type.replace("_", " ")}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Service Type Selection */}
      <div className="grid gap-2">
        <Label htmlFor="service">Service Type</Label>
        <Select value={serviceType} onValueChange={handleServiceTypeChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select service type" />
          </SelectTrigger>
          <SelectContent>
            {serviceTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* CPT Code Selection */}
      <div className="grid gap-2">
        <Label>CPT Codes</Label>
        {serviceType ? (
          <>
            {/* Selected codes display */}
            {selectedCodes.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {selectedCodes.map((code) => (
                  <Badge
                    key={code.code}
                    variant="secondary"
                    className="gap-1 pr-1"
                  >
                    <span className="font-mono">{code.code}</span>
                    <span className="text-muted-foreground">
                      ${code.defaultAmount}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0 hover:bg-transparent"
                      onClick={() => handleRemoveCode(code.code)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            )}

            {/* Add code popover */}
            <Popover open={codeOpen} onOpenChange={setCodeOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start gap-2">
                  <Plus className="h-4 w-4" />
                  Add CPT Code
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search codes..." />
                  <CommandList>
                    <CommandEmpty>No codes found.</CommandEmpty>
                    <CommandGroup>
                      {availableCodes
                        .filter((c) => !selectedCodes.find((sc) => sc.code === c.code))
                        .map((code) => (
                          <CommandItem
                            key={code.code}
                            value={`${code.code} ${code.description}`}
                            onSelect={() => handleAddCode(code)}
                          >
                            <div className="flex w-full items-center justify-between">
                              <div className="flex flex-col">
                                <span className="font-mono font-medium">
                                  {code.code}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {code.description}
                                </span>
                              </div>
                              <span className="text-sm font-medium text-muted-foreground">
                                ${code.defaultAmount}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            Select a service type to see available CPT codes
          </p>
        )}
      </div>

      {/* Total Amount */}
      {selectedCodes.length > 0 && (
        <div className="rounded-lg border border-border bg-muted/50 p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Total Amount</span>
            <span className="text-xl font-bold">${totalAmount.toFixed(2)}</span>
          </div>
        </div>
      )}

      {/* Notes */}
      <div className="grid gap-2">
        <Label htmlFor="notes">Notes (Optional)</Label>
        <Textarea
          id="notes"
          placeholder="Additional claim notes..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={!isValid}>
          Submit Claim
        </Button>
      </div>
    </div>
  );
}
